import { useState } from "react";
import { Button, Input, Label } from "@qrody/ui";
import { useTranslations } from "@/i18n/IntlProvider";
import type { QrCodeResponse } from "@qrody/api-client/codes";

/**
 * Password-protect / remove-protection form for DYNAMIC codes. STATIC
 * codes hide the section upstream — the matrix carries the payload
 * directly so the backend can't enforce a password on scan anyway.
 */
export function CodeProtectionForm({
  code,
  busy,
  onSet,
  onRemove,
}: {
  code: QrCodeResponse;
  busy: boolean;
  onSet: (password: string) => void;
  onRemove: () => void;
}) {
  const t = useTranslations("app.codes.detail.protection");
  const [password, setPassword] = useState("");

  if (code.passwordProtected) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-emerald-700">{t("active")}</span>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => {
            if (!confirm(t("removeConfirm"))) return;
            onRemove();
          }}
        >
          {busy ? t("busy") : t("remove")}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (password.length < 6) return;
        onSet(password);
        setPassword("");
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Label htmlFor="qr-password" className="text-xs">
          {t("passwordLabel")}
        </Label>
        <Input
          id="qr-password"
          type="password"
          minLength={6}
          maxLength={128}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="h-9 w-full"
          required
        />
      </div>
      <Button size="sm" type="submit" disabled={busy || password.length < 6}>
        {busy ? t("busy") : t("set")}
      </Button>
    </form>
  );
}
